import { useEffect, useMemo, useState } from "react";
import {
  GitHubApiError,
  listGitHubInstallations,
  openHealPr,
  type GitHubInstallationView,
  type GitHubLinkedRepo,
  type OpenHealPrInput,
  type RunHealDetail,
} from "../lib/api";

/**
 * "Send heal as PR" dialog, launched from HealReviewPage once every
 * proposal has a decision. The backend is what actually assembles the PR
 * files ({healId}.md + {healId}.json), so this dialog is intentionally
 * lighter than SendAsPrDialog — the user just picks a linked repo and
 * optionally tweaks title / branch / commit message.
 *
 * On success the heal row flips to `pr-opened`, so the caller should
 * refresh to pick up the new status + prUrl.
 */
export function HealPrDialog(props: {
  heal: RunHealDetail;
  onClose: () => void;
  onOpened: (prUrl: string) => void;
}): JSX.Element {
  const { heal, onClose, onOpened } = props;

  const [installations, setInstallations] = useState<GitHubInstallationView[] | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedLinkedId, setSelectedLinkedId] = useState<string>("");
  const [title, setTitle] = useState<string>(
    `UAIP heal: ${heal.totalCount} selector change${heal.totalCount === 1 ? "" : "s"}`,
  );
  const [branch, setBranch] = useState<string>("");
  const [commitMessage, setCommitMessage] = useState<string>("");
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<
    | {
        number: number;
        htmlUrl: string;
        branch: string;
        reusedExistingPr: boolean;
      }
    | null
  >(null);

  const linkedRepos = useMemo((): GitHubLinkedRepo[] => {
    if (!installations) return [];
    return installations.flatMap((i) => i.linkedRepos);
  }, [installations]);

  useEffect(() => {
    let cancelled = false;
    listGitHubInstallations()
      .then((list) => {
        if (cancelled) return;
        setInstallations(list);
        const first = list.flatMap((i) => i.linkedRepos)[0];
        if (first) setSelectedLinkedId(first.id);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(): Promise<void> {
    setError(null);
    setResult(null);
    if (!selectedLinkedId) {
      setError("Pick a linked repo first.");
      return;
    }
    setBusy(true);
    try {
      const input: OpenHealPrInput = {
        linkedRepoId: selectedLinkedId,
        forceUpdateBranch: forceUpdate,
      };
      const trimmedTitle = title.trim();
      if (trimmedTitle) input.title = trimmedTitle;
      const trimmedBranch = branch.trim();
      if (trimmedBranch) input.branch = trimmedBranch;
      const trimmedCommit = commitMessage.trim();
      if (trimmedCommit) input.commitMessage = trimmedCommit;
      const out = await openHealPr(heal.id, input);
      setResult({
        number: out.pr.number,
        htmlUrl: out.pr.htmlUrl,
        branch: out.pr.branch,
        reusedExistingPr: out.pr.reusedExistingPr,
      });
      onOpened(out.pr.htmlUrl);
    } catch (err) {
      if (err instanceof GitHubApiError) {
        setError(friendlyHealPrError(err));
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-xl border border-slate-800 bg-slate-950 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-medium text-slate-100">
            Send heal as GitHub PR
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Close
          </button>
        </header>

        <div className="mb-3 rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-400">
          The PR will contain two files:{" "}
          <span className="font-mono text-slate-200">
            uaip-heals/{heal.id}.md
          </span>{" "}
          and{" "}
          <span className="font-mono text-slate-200">
            uaip-heals/{heal.id}.json
          </span>{" "}
          — a human summary plus a machine-applicable selector mapping.
        </div>

        {loadError && (
          <Banner kind="error">
            Couldn't load linked repos: {loadError}
          </Banner>
        )}

        {installations !== null && linkedRepos.length === 0 && !loadError && (
          <Banner kind="info">
            No linked repos yet. Head to{" "}
            <a className="underline" href="/account">
              Account → GitHub integration
            </a>{" "}
            to link one.
          </Banner>
        )}

        {result ? (
          <div className="space-y-3">
            <Banner kind="success">
              {result.reusedExistingPr
                ? `Updated existing PR #${result.number}.`
                : `Opened PR #${result.number}.`}
            </Banner>
            <a
              href={result.htmlUrl}
              target="_blank"
              rel="noopener"
              className="inline-flex rounded-md bg-indigo-500 px-3 py-1.5 text-sm text-white hover:bg-indigo-400"
            >
              View PR →
            </a>
            <div className="text-xs text-slate-500">
              Branch:{" "}
              <span className="font-mono text-slate-300">{result.branch}</span>
            </div>
          </div>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <Field label="Linked repo">
              <select
                value={selectedLinkedId}
                onChange={(e) => setSelectedLinkedId(e.target.value)}
                disabled={linkedRepos.length === 0}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              >
                {linkedRepos.length === 0 && (
                  <option value="">(no linked repos)</option>
                )}
                {linkedRepos.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.fullName} (default {r.defaultBranch})
                  </option>
                ))}
              </select>
            </Field>

            <Field label="PR title (optional)">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="UAIP heal: …"
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Branch (optional)">
                <input
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  placeholder={`uaip/heal-${heal.id}-…`}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 font-mono"
                />
              </Field>
              <Field label="Commit message (optional)">
                <input
                  type="text"
                  value={commitMessage}
                  onChange={(e) => setCommitMessage(e.target.value)}
                  placeholder="(defaults to PR title)"
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                type="checkbox"
                checked={forceUpdate}
                onChange={(e) => setForceUpdate(e.target.checked)}
              />
              Force-update the branch if it already exists
            </label>

            {error && <Banner kind="error">{error}</Banner>}

            <div className="mt-1 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || linkedRepos.length === 0}
                className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm text-white hover:bg-indigo-400 disabled:opacity-50"
              >
                {busy ? "Opening PR…" : "Open PR"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function friendlyHealPrError(err: GitHubApiError): string {
  switch (err.code) {
    case "GITHUB_DISABLED":
      return "GitHub App isn't configured on this deployment.";
    case "GITHUB_REINSTALL_REQUIRED":
      return "The installation token was rejected by GitHub — please re-connect the app from the Account page.";
    case "GITHUB_BRANCH_EXISTS":
      return 'That branch already exists on GitHub. Pick a different name or check "force-update".';
    case "GITHUB_NOT_FOUND":
      return `Repository or base branch not found on GitHub (${err.message}).`;
    case "HEAL_INCOMPLETE":
      return "Every proposal needs a decision before you can open a PR.";
    case "HEAL_DISCARDED":
      return "This heal was discarded. Re-open it by re-running the heal.";
    case "HEAL_ALREADY_MERGED":
      return "This heal is already merged.";
    case "GITHUB_UPSTREAM":
    default:
      return `GitHub upstream error: ${err.message}`;
  }
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wide text-slate-400">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function Banner({
  kind,
  children,
}: {
  kind: "error" | "info" | "success";
  children: React.ReactNode;
}): JSX.Element {
  const cls =
    kind === "error"
      ? "border-rose-800/60 bg-rose-950/50 text-rose-200"
      : kind === "success"
        ? "border-emerald-800/60 bg-emerald-950/40 text-emerald-200"
        : "border-slate-700 bg-slate-900 text-slate-300";
  return (
    <div className={`rounded-md border ${cls} px-3 py-2 text-xs`}>{children}</div>
  );
}
